
import { ActivityCodeApi } from "@/api/ActivityCodeApi";
import { ClientApi } from "@/api/ClientApi";
import { CurrencyApi } from "@/api/CurrencyApi";
import { SalemanApi } from "@/api/SalemenApi";
import { FormConfigType } from "@/types/dynamicForm";
import { CUSTOM_ELEMENT, HIDDEN_INPUT_TYPE, PHONE_INPUT_TYPE, RADIO_INPUT_TYPE, SELECT_INPUT_TYPE, VALIDATION_ERROR_MESSAGE, Yup } from "@mavichpe/dynamic-forms";
import { ACTIVITY_CODE_LENGTH, EINVOICE_EXPORT_INVOICE_TYPE_CODE, EINVOICE_INVOICE_TYPE_CODE, EINVOICE_TYPES, INVOICE_CREDIT_TYPE_CODE, INVOICE_HOLDING_TYPE_CODE, INVOICE_TYPES, RECEPTOR_TAX_REGIME_GENERAL, RECEPTOR_TAX_REGIME_OPTIONS, TIPO_DE_IDENTIFICACION_EXTRANJERO_NO_DOMICILIADO, TIPOS_DE_IDENTIFICACION } from "@utils/Constants";
import { generateCurrencyOptionLabel } from "@utils/Currency";
import { identificationRequiredWhen, optionalPhoneForEinvoice, phoneRequiredForEinvoice } from "@utils/Forms";
import { CurrencyIdPubSub, OpenAddClientPubSub, OpenSearchClientByIdentificationPubSub } from "@utils/pubsubs/PubSub";
import { Button } from "reactstrap";

/**
 * El nodo "Identificacion" del receptor es obligatorio en la Factura Electronica y,
 * desde v4.4, tambien en la Factura Electronica de Exportacion. En el tiquete no.
 */
const requiresReceptorIdentification = (einvoice_kind: unknown): boolean =>
    einvoice_kind == EINVOICE_INVOICE_TYPE_CODE || einvoice_kind == EINVOICE_EXPORT_INVOICE_TYPE_CODE;

const InvoiceDataFormConfig: FormConfigType = {
    sections: [
        {
            "fields": [
                [
                    {
                        "type": RADIO_INPUT_TYPE,
                        "name": "tipo",
                        "validation": Yup.string().required(),
                        "label": "Tipo Factura",
                        "isHorizontalValues": true,
                        "labelSizing": {
                            "sm": {
                                "size": 2
                            }
                        },
                        "sizing": {
                            "sm": {
                                "size": 6
                            }
                        },
                        "options": INVOICE_TYPES,
                        "defaultValue": 0,
                    },
                    {
                        "type": SELECT_INPUT_TYPE,
                        "name": "currencyId",
                        "label": "Moneda",
                        "optionsFunction": () => CurrencyApi.optionsList({}, (i) => generateCurrencyOptionLabel(i)),
                        "onChangePubSub": (value: string) => CurrencyIdPubSub.publish({ currencyId: value }),
                        "labelSizing": {
                            "sm": 2
                        },
                        "sizing": {
                            "sm": 3
                        },
                        "validation": Yup.string().required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR),
                    },
                    {
                        "type": "number",
                        "isDecimal": true,
                        "name": "exchange_rate",
                        "min": "0",
                        "step": "0.01",
                        "label": "Tipo de Cambio",
                        "labelSizing": {
                            "sm": 2
                        },
                        "sizing": {
                            "sm": 3,
                            "xl": 2,
                        },
                        "validation": Yup.string().required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR),
                    },
                ],
                [
                    {
                        "type": "number",
                        "name": "plazo",
                        "min": "1",
                        "isHorizontal": true,
                        "suffix": "dias",
                        "visibleIf": {
                            "tipo": INVOICE_CREDIT_TYPE_CODE
                        },
                        "label": "Plazo",
                        "labelSizing": {
                            "sm": 2,
                            "md": 1,
                        },
                        "sizing": {
                            "sm": 4,
                            "md": 3,
                            "lg": 2,
                        },
                        "validation": Yup.string().required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR),
                    },
                    {
                        "type": "money",
                        "name": "monto_inicial",
                        "isHorizontal": true,
                        "min": "1",
                        "visibleIf": {
                            "tipo": INVOICE_HOLDING_TYPE_CODE
                        },
                        "label": "Abono Inicial",
                        "labelSizing": {
                            "sm": 2,
                            "md": 2,
                            "lg": 2,
                            "xl": 1,
                        },
                        "sizing": {
                            "sm": 4,
                            "md": 3,
                            "lg": 2,
                        },
                        "validation": Yup.number().when("tipo", {
                            is: (tipo: unknown) => tipo == INVOICE_HOLDING_TYPE_CODE,
                            then: (n) => n.required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR).min(0, "El monto del abono debe ser mayor a 0"),
                            otherwise: (s) => s.notRequired(),
                        }),
                    },
                ],
                [
                    {
                        "type": RADIO_INPUT_TYPE,
                        "name": "einvoice_kind",
                        "validation": Yup.string().required(),
                        "label": "Tipo Factura Electronica",
                        "isHorizontalValues": true,
                        "labelSizing": {
                            "sm": {
                                "size": 2
                            }
                        },
                        "sizing": {
                            "sm": {
                                "size": 4
                            }
                        },
                        "options": EINVOICE_TYPES,
                        "defaultValue": 0,
                    },
                    {
                        "type": "text",
                        "name": "reference_number",
                        "label": "Numero Referencia",
                        "visibilityHiddenIf": () => true, // by default this input is hidden, suscrption data make it visible
                        "labelSizing": {
                            "sm": {
                                "size": 2
                            }
                        },
                        "sizing": {
                            "sm": {
                                "size": 2
                            }
                        },
                    },
                    {
                        "type": SELECT_INPUT_TYPE,
                        "name": "salemanId",
                        "label": "Vendedor",
                        "optionsFunction": () => SalemanApi.optionsList(),
                        "labelSizing": {
                            "sm": {
                                "size": 2
                            }
                        },
                        "sizing": {
                            "sm": {
                                "size": 3
                            }
                        },
                    },
                    {
                        "type": SELECT_INPUT_TYPE,
                        "name": "activitycode",
                        // Sin defaultValue: cualquier valor aquí se siembra en el form aunque sea 0
                        // (el select no lo muestra) y Yup lo da por válido al castearlo a "0".
                        "validation": Yup.string().required()
                            .length(ACTIVITY_CODE_LENGTH, "Se permite solo 6 caracteres para este codigo"),
                        "label": "Actividad Economica",
                        "optionsFunction": () => ActivityCodeApi.optionsList({}, undefined, "code"),
                        "labelSizing": {
                            "sm": {
                                "size": 2
                            }
                        },
                        "sizing": {
                            "sm": {
                                "size": 3
                            }
                        },
                    },
                ],
                [
                    {
                        // Se carga del cliente y se puede cambiar por documento: rebaja las lineas
                        // que gravan mas que la tarifa que la ley fija para este comprador.
                        "type": SELECT_INPUT_TYPE,
                        "name": "tax_regime",
                        "label": "Tarifa especial de IVA",
                        "defaultValue": RECEPTOR_TAX_REGIME_GENERAL,
                        "options": RECEPTOR_TAX_REGIME_OPTIONS,
                        "labelSizing": {
                            "sm": {
                                "size": 2
                            }
                        },
                        "sizing": {
                            "sm": {
                                "size": 5
                            }
                        },
                    },
                ],
                [
                    {
                        "type": "checkbox",
                        "name": "with_user_info",
                        "label": "Imprimir informacion del cliente en el tiquete?",
                        // "validation": Yup.string().required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR),
                    }
                ]
            ]
        },
        {
            title: "Datos de Cliente",
            "fields": [
                [
                    {
                        "type": "text",
                        "name": "client_code",
                        "exludeFromSubmit": true,
                        "label": "Cod",
                        "labelSizing": {
                            "sm": {
                                "size": 2
                            }
                        },
                        "sizing": {
                            "sm": {
                                "size": 2
                            }
                        }
                    },
                    {
                        "type": SELECT_INPUT_TYPE,
                        "name": "clientId",
                        "label": "Cliente",
                        "optionsFunction": () => ClientApi.optionsList(),
                        "labelSizing": {
                            "sm": 2
                        },
                        "sizing": {
                            "sm": 9
                        },
                        "validation": Yup.string().required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR),
                    },
                    {
                        "type": CUSTOM_ELEMENT,
                        "label": " ",
                        "labelSizing": {
                            "sm": 2
                        },
                        "sizing": {
                            "sm": 1
                        },
                        "element": <Button
                            className="mt-2"
                            block
                            color="primary"
                            type="button"
                            onClick={() => OpenAddClientPubSub.publish({ openOffCanvas: true })}>
                            Crear
                        </Button>
                    },
                ],
                [
                    {
                        "type": "text",
                        "name": "nombre_factura",
                        "label": "Nombre Facturacion",
                        "labelSizing": {
                            "sm": {
                                "size": 2
                            }
                        },
                        "sizing": {
                            "sm": {
                                "size": 12
                            }
                        }
                    },
                ],
                [
                    {
                        // La razon social se guarda aparte del nombre de facturacion porque
                        // Hacienda espera el nombre legal en Receptor/Nombre y el comercial
                        // en Receptor/NombreComercial. No se edita: sale de la ficha del cliente.
                        "type": HIDDEN_INPUT_TYPE,
                        "name": "razon_social",
                    },
                ],
                [
                    {
                        "type": PHONE_INPUT_TYPE,
                        "name": "tel1",
                        "label": "Telefono 1",
                        "validation": phoneRequiredForEinvoice(),
                    },
                    {
                        "type": PHONE_INPUT_TYPE,
                        "name": "tel2",
                        "label": "Telefono 2",
                        "validation": optionalPhoneForEinvoice(),
                    },
                    {
                        "type": "text",
                        "name": "email",
                        "label": "Email",
                        "validation": Yup.string().when("einvoice_kind", {
                            is: (einvoice_kind: unknown) => einvoice_kind == EINVOICE_INVOICE_TYPE_CODE,
                            then: (s) => s.required("El campo de email es obligatorio")
                                .test("is-valid-emails", "Uno o más correos no son válidos", (value) => {
                                    if (!value) return false;

                                    // Separar por coma y limpiar espacios
                                    const emails = value.split(",").map((email) => email.trim());

                                    // Validar cada correo usando una expresión regular o Yup
                                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                                    return emails.every((email) => emailRegex.test(email));
                                }),
                            otherwise: (s) => s.notRequired(),
                        }),
                    },
                ],
                [
                    {
                        "type": SELECT_INPUT_TYPE,
                        "name": "tipo_identificacion",
                        "label": "Tipo Identificacion",
                        "options": TIPOS_DE_IDENTIFICACION,
                        "sizing": {
                            "sm": 4
                        },
                        "validation": Yup.string().when("einvoice_kind", {
                            is: requiresReceptorIdentification,
                            then: (s) => s.required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR),
                            otherwise: (s) => s.notRequired(),
                        }),
                    },
                    {
                        "type": "text",
                        "name": "identificacion",
                        "label": "Numero Identificacion",
                        "sizing": {
                            "sm": 4
                        },
                        "validation": identificationRequiredWhen(requiresReceptorIdentification),
                        "suffixButton": true,
                        "suffix": <Button
                            className="rounded-0"
                            block
                            color="primary"
                            type="button"
                            onClick={() => OpenSearchClientByIdentificationPubSub.publish({ openOffCanvas: true })}>
                            Buscar
                        </Button>
                    },
                    {
                        "type": "text",
                        "name": "clientActivitycode",
                        "label": "Codigo Actividad Economica",
                        "maxlength": 6,
                        "sizing": {
                            "sm": 4
                        },
                        "validation": Yup.string().when("einvoice_kind", {
                            is: (einvoice_kind: unknown) => einvoice_kind == EINVOICE_INVOICE_TYPE_CODE,
                            then: (s) => s.required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR)
                                .length(ACTIVITY_CODE_LENGTH, "Se permite solo 6 caracteres para este codigo"),
                            otherwise: (s) => s.notRequired(),
                        }),
                    },
                ],
                [
                    {
                        "type": "text",
                        "name": "direccion",
                        "label": "Dirección",
                        "labelSizing": {
                            "sm": {
                                "size": 2
                            }
                        },
                        "sizing": {
                            "sm": {
                                "size": 12
                            }
                        },
                        "validation": Yup.string().when("tipo_identificacion", {
                            is: (tipo_identificacion: unknown) => tipo_identificacion == TIPO_DE_IDENTIFICACION_EXTRANJERO_NO_DOMICILIADO,
                            then: (s) => s.required("Para extranjeros no domiciliados, es obligatorio la informacion de la direcciona")
                                .min(5, "Es requerido al menos 5 caracteres para la direccion")
                                .max(300, "El tamaño maximo para la direccion de 300 caracteres, por favor reduzca el tamaño de la direccion"),
                            otherwise: (s) => s.notRequired(),
                        })
                    },
                ],
            ]
        }
    ],
};


export default InvoiceDataFormConfig;